/*
 * Copyright 2026 Sony Corporation
 */
import type { Device, Project, RoadmapActionRequest, Roadmap, Run, Version } from '@ips-jit/schema';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { DeviceScreen } from '../components/DeviceScreen';
import { FoldedStrip } from '../components/FoldedStrip';
import { GeneratedApps } from '../components/GeneratedApps';
import { ProjectChat, stageAnchor } from '../components/ProjectChat';
import { ProjectHeader } from '../components/ProjectHeader';
import { RoadmapPanel } from '../components/RoadmapPanel';
import { useAuth } from '../contexts/auth-context';
import { lastDevice, pickDevice } from '../services/device-choice';

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

const FAST_REFRESH_MS = 4_000;
const IDLE_REFRESH_MS = 20_000;
/** The device's agent reports changes at once, so the page need not ask often to show them soon. */
const DEVICE_REFRESH_MS = 10_000;

/** A version is still moving until its build has ended and the device has answered. */
/** Below this window width the chat would get too narrow beside both side columns, so the screen starts folded. */
const WIDE_WINDOW = 1440;

const inFlight = (v: Version): boolean =>
  v.status === 'building' ||
  (v.status === 'built' && (v.deployment_state === 'sent' || v.deployment_state === 'downloading'));

/**
 * The project's name and runtime on one line, then three columns: the roadmap over the app's builds, the conversation
 * with the device log, and the runtime screen. The side columns fold to strips.
 */
export const ProjectDetail = () => {
  const { api } = useAuth();
  const { projectId } = useParams();
  const [project, setProject] = useState<Project>();
  const [devices, setDevices] = useState<Device[]>([]);
  const [versions, setVersions] = useState<Version[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= WIDE_WINDOW);
  const [chosenBuild, setChosenBuild] = useState<number>();
  const [progress, setProgress] = useState<{ run: Run | null; roadmap: Roadmap | null; step?: string }>({
    run: null,
    roadmap: null,
  });
  const [chatKey, setChatKey] = useState(0);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!projectId) {
      return;
    }
    api.getProject(projectId).then(setProject, (caught: unknown) => {
      setError(describe(caught, 'Could not load the project.'));
    });
  }, [api, projectId]);

  useEffect(() => {
    const load = () => api.listDevices().then(setDevices, () => undefined);
    void load();
    const timer = setInterval(() => void load(), DEVICE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [api]);

  // The project's own runtime; for projects from before runtimes were chosen, the device used last, else the online
  // one seen most recently, and the user can still pick another.
  useEffect(() => {
    if (project?.runtime_id) {
      setDeviceId(project.runtime_id);
    } else if (projectId && deviceId === '' && devices.length > 0) {
      setDeviceId(pickDevice(devices, lastDevice(projectId)));
    }
  }, [devices, deviceId, projectId, project?.runtime_id]);

  const loadVersions = useCallback(async () => {
    if (!projectId) {
      return;
    }
    try {
      setVersions(await api.listVersions(projectId));
    } catch (caught) {
      setError(describe(caught, 'Could not load builds.'));
    }
  }, [api, projectId]);

  // A new build is shown as it arrives, whichever was chosen before.
  const newest = versions[0]?.build;
  useEffect(() => setChosenBuild(undefined), [newest]);

  const selectVersion = useCallback(
    (version: number) => {
      const build = versions.find((v) => v.version === version)?.build;
      if (build !== undefined) {
        setChosenBuild(build === newest ? undefined : build);
      }
    },
    [versions, newest]
  );

  const selectStep = useCallback((stepId: string) => {
    document.getElementById(stageAnchor(stepId))?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  const watching = versions.some(inFlight);
  useEffect(() => {
    void loadVersions();
    const timer = setInterval(() => void loadVersions(), watching ? FAST_REFRESH_MS : IDLE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadVersions, watching]);

  const deployAgain = async (build: number) => {
    if (!projectId) {
      return;
    }
    setError(undefined);
    try {
      await api.deployVersion(projectId, build);
      await loadVersions();
    } catch (caught) {
      setError(describe(caught, 'Could not send the build.'));
    }
  };

  const roadmapAction = useCallback(
    async (action: RoadmapActionRequest) => {
      if (!projectId) {
        return;
      }
      setError(undefined);
      try {
        await api.roadmapAction(projectId, action);
        setChatKey((k) => k + 1);
      } catch (caught) {
        setError(describe(caught, 'Could not change the roadmap.'));
      }
    },
    [api, projectId]
  );

  if (!project) {
    return error ? (
      <Stack spacing={2}>
        <Alert severity="error">{error}</Alert>
        <Button component={RouterLink} to="/projects" sx={{ alignSelf: 'flex-start' }}>
          Back to projects
        </Button>
      </Stack>
    ) : (
      <Typography color="text.secondary">Loading...</Typography>
    );
  }

  return (
    <Stack spacing={1} sx={{ height: { md: '100%' } }}>
      <ProjectHeader project={project} devices={devices} deviceId={deviceId} onDeviceChange={setDeviceId} />
      {error && <Alert severity="error">{error}</Alert>}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            md: `${leftOpen ? '300px' : '48px'} minmax(0, 1fr) ${rightOpen ? 'minmax(400px, 28%)' : '48px'}`,
          },
          gridTemplateRows: { md: 'minmax(0, 1fr)' },
        }}
      >
        {leftOpen ? (
          <Stack spacing={2} sx={{ minHeight: 0 }}>
            <RoadmapPanel
              roadmap={progress.roadmap}
              step={progress.step}
              onAction={roadmapAction}
              onSelectVersion={selectVersion}
              onSelectStep={selectStep}
              onFold={() => setLeftOpen(false)}
            />
            <GeneratedApps
              projectId={project.project_id}
              versions={versions}
              devices={devices}
              onDeployAgain={(v) => void deployAgain(v)}
              chosen={chosenBuild}
              onChoose={setChosenBuild}
            />
          </Stack>
        ) : (
          <FoldedStrip label="Roadmap and app" side="left" onOpen={() => setLeftOpen(true)} />
        )}
        <ProjectChat
          projectId={project.project_id}
          deviceId={deviceId}
          errorsOnly
          onProgress={setProgress}
          refreshKey={chatKey}
        />
        {rightOpen ? (
          <DeviceScreen
            projectId={project.project_id}
            running={versions.some((v) => v.version !== null && v.deployment_state === 'applied')}
            onFold={() => setRightOpen(false)}
          />
        ) : (
          <FoldedStrip label="Runtime screen" side="right" onOpen={() => setRightOpen(true)} />
        )}
      </Box>
    </Stack>
  );
};
